import {
  CONTENT_DELIVERABLE_OPTIONS_BY_PLATFORM,
  isLivestreamContentType,
  parseContentDeliverables,
} from '../shared/campaignContentDeliverables';

const PLATFORM_ORDER = [...Object.keys(CONTENT_DELIVERABLE_OPTIONS_BY_PLATFORM), 'UGC'];

function sortPlatformGroups(groups) {
  const orderIndex = new Map(PLATFORM_ORDER.map((platform, index) => [platform, index]));

  return groups.sort((left, right) => {
    const leftIndex = orderIndex.has(left.platform)
      ? orderIndex.get(left.platform)
      : Number.MAX_SAFE_INTEGER;
    const rightIndex = orderIndex.has(right.platform)
      ? orderIndex.get(right.platform)
      : Number.MAX_SAFE_INTEGER;

    if (leftIndex !== rightIndex) return leftIndex - rightIndex;
    return left.platform.localeCompare(right.platform);
  });
}

export function groupByPlatform(items = [], getPlatform = (item) => item.platform) {
  const groups = new Map();

  items.forEach((item) => {
    const platform = getPlatform(item);
    if (!platform) return;

    if (!groups.has(platform)) {
      groups.set(platform, { platform, items: [] });
    }

    groups.get(platform).items.push(item);
  });

  return sortPlatformGroups([...groups.values()]);
}

const FEE_TYPE_LABELS = {
  commission_rate: 'Commission',
  flat_rate: 'Flat Rate',
  hourly_rate: 'Hourly',
};

export function normalizeInfluencerOffering(offering, index = 0) {
  if (!offering) return null;

  let feeModels = [];
  if (Array.isArray(offering.fee_models)) {
    feeModels = offering.fee_models;
  } else if (Array.isArray(offering.feeModels)) {
    feeModels = offering.feeModels;
  }

  const contentType = offering.content_type || offering.contentType || '';
  const isLivestream = isLivestreamContentType(contentType);

  return {
    id: offering.id || `${offering.platform}-${contentType}-${index}`,
    platform: offering.platform || '',
    contentType,
    feeModels: feeModels
      .map((feeModel) => ({
        type: feeModel.type,
        rate: Number.parseFloat(feeModel.rate),
      }))
      .filter((feeModel) => feeModel.type && Number.isFinite(feeModel.rate) && feeModel.rate > 0)
      .filter((feeModel) => isLivestream || feeModel.type !== 'commission_rate'),
  };
}

function hasLegacyShortVideoRates(influencer) {
  return Boolean(
    influencer?.short_video_flat_rate
    || influencer?.short_video_hourly_rate,
  );
}

function hasLegacyLivestreamRates(influencer) {
  return Boolean(
    influencer?.livestream_commission_rate
    || influencer?.livestream_flat_rate
    || influencer?.livestream_hourly_rate,
  );
}

function getLegacyPlatform(influencer) {
  if (influencer?.tiktok_handle || influencer?.tiktokHandle) return 'TikTok';
  if (influencer?.instagram_handle || influencer?.instagramHandle) return 'Instagram';
  if (influencer?.whatnot_handle || influencer?.whatnotHandle) return 'Whatnot';
  return 'TikTok';
}

function getShortFormContentType(platform) {
  if (platform === 'Instagram') return 'Instagram Reel';
  return 'TikTok Video';
}

function getLivestreamContentTypeForPlatform(platform) {
  if (platform === 'Instagram') return 'Instagram Live';
  if (platform === 'Whatnot') return 'Whatnot Livestream';
  return 'TikTok Live';
}

function legacyInfluencerToOfferings(influencer) {
  const deliverables = parseContentDeliverables(
    influencer?.content_deliverables ?? influencer?.contentDeliverables,
  ).map((deliverable) => ({
    platform: deliverable.platform,
    contentType: deliverable.contentType || deliverable.content_type || '',
  }));

  if (deliverables.length === 0) {
    const platform = getLegacyPlatform(influencer);

    if (hasLegacyShortVideoRates(influencer)) {
      deliverables.push({
        platform,
        contentType: getShortFormContentType(platform),
      });
    }

    if (hasLegacyLivestreamRates(influencer)) {
      deliverables.push({
        platform,
        contentType: getLivestreamContentTypeForPlatform(platform),
      });
    }
  }

  const feeModels = influencer?.fee_models || influencer?.feeModels || [];
  const effectiveFeeModels = feeModels.length > 0
    ? feeModels
    : [
      ...(hasLegacyShortVideoRates(influencer) || hasLegacyLivestreamRates(influencer)
        ? [
          ...(influencer?.livestream_commission_rate ? ['commission'] : []),
          ...(influencer?.short_video_flat_rate || influencer?.livestream_flat_rate ? ['flat'] : []),
          ...(influencer?.short_video_hourly_rate || influencer?.livestream_hourly_rate ? ['hourly'] : []),
        ]
        : []),
    ];

  return deliverables.map((deliverable, index) => {
    const contentType = deliverable.contentType || deliverable.content_type || '';
    const isLivestream = isLivestreamContentType(contentType);
    const normalizedFeeModels = [];

    effectiveFeeModels.forEach((entry) => {
      const legacyType = typeof entry === 'string' ? entry : entry?.type;
      let type = legacyType;
      let rate = null;

      if (legacyType === 'commission' || legacyType === 'commission_rate') {
        if (!isLivestream) return;
        type = 'commission_rate';
        rate = influencer?.livestream_commission_rate;
      } else if (legacyType === 'flat' || legacyType === 'flat_rate') {
        type = 'flat_rate';
        rate = isLivestream
          ? influencer?.livestream_flat_rate
          : influencer?.short_video_flat_rate;
      } else if (legacyType === 'hourly' || legacyType === 'hourly_rate') {
        type = 'hourly_rate';
        rate = isLivestream
          ? influencer?.livestream_hourly_rate
          : influencer?.short_video_hourly_rate;
      }

      const parsedRate = Number.parseFloat(rate);
      if (type && Number.isFinite(parsedRate) && parsedRate > 0) {
        normalizedFeeModels.push({ type, rate: parsedRate });
      }
    });

    return normalizeInfluencerOffering({
      platform: deliverable.platform,
      content_type: contentType,
      fee_models: normalizedFeeModels,
    }, index);
  }).filter(Boolean);
}

export function getInfluencerOfferings(influencer) {
  if (Array.isArray(influencer?.offerings) && influencer.offerings.length > 0) {
    return influencer.offerings
      .map((offering, index) => normalizeInfluencerOffering(offering, index))
      .filter((offering) => offering?.platform && offering?.contentType);
  }

  return legacyInfluencerToOfferings(influencer);
}

export function influencerHasLivestreamOffering(influencer) {
  return getInfluencerOfferings(influencer)
    .some((offering) => isLivestreamContentType(offering.contentType));
}

export function influencerHasShortFormOffering(influencer) {
  return getInfluencerOfferings(influencer)
    .some((offering) => offering.contentType && !isLivestreamContentType(offering.contentType));
}

export function getInfluencerContentTypeLabels(influencer) {
  const labels = [];
  const offerings = getInfluencerOfferings(influencer);

  offerings.forEach((offering) => {
    if (offering.contentType && !labels.includes(offering.contentType)) {
      labels.push(offering.contentType);
    }
  });

  return labels;
}

export function getInfluencerCampaignTypeLabels(influencer) {
  const labels = [];
  if (influencerHasShortFormOffering(influencer)) labels.push('Short Video');
  if (influencerHasLivestreamOffering(influencer)) labels.push('Livestream');
  return labels;
}

export function formatOfferingFeeModel(feeModel) {
  if (!feeModel?.type || !Number.isFinite(feeModel.rate)) return null;

  if (feeModel.type === 'commission_rate') return `${feeModel.rate}%`;
  if (feeModel.type === 'hourly_rate') return `$${feeModel.rate}/hr`;
  if (feeModel.type === 'flat_rate') return `$${feeModel.rate}`;
  return `$${feeModel.rate}`;
}

export function getOfferingRateKey(platform, contentType) {
  return `${platform}::${contentType}`;
}

export function buildOfferingsFromProfileRatesForm({
  contentDeliverables = [],
  feeModels = [],
  offeringRates = {},
}) {
  const deliverables = parseContentDeliverables(contentDeliverables);

  return deliverables.map((deliverable) => {
    const contentType = deliverable.contentType || deliverable.content_type || '';
    const isLivestream = isLivestreamContentType(contentType);
    const key = getOfferingRateKey(deliverable.platform, contentType);
    const ratesForOffering = offeringRates[key] || {};
    const offeringFeeModels = [];

    feeModels.forEach((entry) => {
      const legacyType = typeof entry === 'string' ? entry : entry?.type;
      let type = legacyType;

      if (legacyType === 'commission' || legacyType === 'commission_rate') {
        if (!isLivestream) return;
        type = 'commission_rate';
      } else if (legacyType === 'flat' || legacyType === 'flat_rate') {
        type = 'flat_rate';
      } else if (legacyType === 'hourly' || legacyType === 'hourly_rate') {
        type = 'hourly_rate';
      }

      const rate = Number.parseFloat(String(ratesForOffering[type] || '').replace(/[$%]/g, ''));
      if (type && Number.isFinite(rate) && rate > 0) {
        offeringFeeModels.push({ type, rate });
      }
    });

    return {
      platform: deliverable.platform,
      content_type: contentType,
      fee_models: offeringFeeModels,
    };
  }).filter((offering) => offering.platform && offering.content_type);
}

export function offeringRatesFormFromOfferings(offerings = []) {
  const rates = {};

  getInfluencerOfferings({ offerings }).forEach((offering) => {
    const key = getOfferingRateKey(offering.platform, offering.contentType);
    rates[key] = {};
    offering.feeModels.forEach((feeModel) => {
      rates[key][feeModel.type] = String(feeModel.rate);
    });
  });

  return rates;
}

export function getOfferingRateFieldConfigs(contentType, feeModels = []) {
  const isLivestream = isLivestreamContentType(contentType);
  const fields = [];

  if (feeModels.includes('commission') && isLivestream) {
    fields.push({
      type: 'commission_rate',
      label: 'Commission (%)',
      adornment: 'suffix',
      suffix: '%',
    });
  }

  if (feeModels.includes('flat')) {
    fields.push({
      type: 'flat_rate',
      label: 'Flat Rate ($)',
      adornment: 'prefix',
      prefix: '$',
    });
  }

  if (feeModels.includes('hourly') && isLivestream) {
    fields.push({
      type: 'hourly_rate',
      label: 'Hourly Rate ($)',
      adornment: 'prefix',
      prefix: '$',
    });
  }

  return fields;
}

export function buildRatePackagesFromOfferings(influencer) {
  return getInfluencerOfferings(influencer).flatMap((offering) => (
    offering.feeModels.map((feeModel) => {
      const priceType = FEE_TYPE_LABELS[feeModel.type] || feeModel.type;
      const price = formatOfferingFeeModel(feeModel);
      if (!price) return null;

      return {
        id: `${offering.platform}-${offering.contentType}-${feeModel.type}`,
        title: offering.contentType,
        priceType,
        price,
        rawPrice: feeModel.type === 'commission_rate' ? null : feeModel.rate,
        format: isLivestreamContentType(offering.contentType) ? 'Livestream' : 'Short Video',
        platform: offering.platform,
        contentType: offering.contentType,
        description: `${priceType} for ${offering.contentType}.`,
      };
    }).filter(Boolean)
  ));
}

export function legacyProfileFormFromOfferings(offerings = []) {
  const normalized = offerings.map((offering, index) => normalizeInfluencerOffering(offering, index));
  const contentDeliverables = normalized.map((offering) => ({
    platform: offering.platform,
    contentType: offering.contentType,
  }));

  const feeModelSet = new Set();
  const form = {
    contentDeliverables,
    platformTypes: [...new Set(contentDeliverables.map((item) => item.platform))],
    feeModels: [],
    shortVideoCommission: '',
    shortVideoFlat: '',
    shortVideoHourly: '',
    livestreamCommission: '',
    livestreamFlat: '',
    livestreamHourly: '',
  };

  normalized.forEach((offering) => {
    const isLivestream = isLivestreamContentType(offering.contentType);

    offering.feeModels.forEach((feeModel) => {
      if (feeModel.type === 'commission_rate' && isLivestream) feeModelSet.add('commission');
      if (feeModel.type === 'flat_rate') feeModelSet.add('flat');
      if (feeModel.type === 'hourly_rate') feeModelSet.add('hourly');

      if (feeModel.type === 'commission_rate' && isLivestream) {
        form.livestreamCommission = String(feeModel.rate);
      } else if (feeModel.type === 'flat_rate') {
        if (isLivestream) form.livestreamFlat = String(feeModel.rate);
        else form.shortVideoFlat = String(feeModel.rate);
      } else if (feeModel.type === 'hourly_rate') {
        if (isLivestream) form.livestreamHourly = String(feeModel.rate);
        else form.shortVideoHourly = String(feeModel.rate);
      }
    });
  });

  form.feeModels = [...feeModelSet];
  return form;
}
